// The billing and usage screen.
//
// It knows nothing about what is on it. Everything comes from `SECTIONS`,
// filtered by whether the section has anything to say, so the page is short for
// a workspace that has never opened the studio and long for one that lives in
// it — and adding a section later touches `sections.tsx` and `workspace_usage()`,
// never this file.
//
// THE PERMISSION IS CHECKED TWICE AND ONLY ONE OF THEM COUNTS. The early return
// below reads `can("billing.view")` from the workspace context, which exists so
// the reader gets a sentence instead of a spinner followed by an error. The
// check that actually protects the data is inside `workspace_usage()`, which
// raises PT403 — so a stale permission list, a hand-typed URL, or a page left
// open while somebody's role was changed all end in a refusal from PostgreSQL.
// `BillingForbiddenError` is that refusal, and it renders the same sentence.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../lib/WorkspaceContext";
import { logError } from "../lib/logger";
import { isForbidden, loadUsage } from "./api";
import { SECTIONS } from "./sections";
import type { WorkspaceUsage } from "./types";

/** The windows worth offering. The server clamps at 180 regardless. */
const RANGES: Array<{ days: number; label: string }> = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

/** The sentence a reader without the permission gets. Shared by both checks. */
function NoAccess() {
  return (
    <div className="card muted">
      <h1 className="bill-h1">Billing &amp; usage</h1>
      <p>
        You do not have permission to see this workspace's plan and usage. It is limited to the
        people responsible for paying for it — an owner or an admin can grant{" "}
        <code>billing.view</code> from <Link to="/people">People</Link>.
      </p>
    </div>
  );
}

export function Billing() {
  const { active, can, ready } = useWorkspace();
  const workspaceId = active?.workspace_id ?? null;

  const [days, setDays] = useState(30);
  const [data, setData] = useState<WorkspaceUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      setData(await loadUsage(workspaceId, days));
      setError(null);
      setForbidden(false);
    } catch (e: any) {
      // A refusal is not a fault. It is the correct answer to a question this
      // account was not entitled to ask, and paging anyone about it — or
      // showing a red error card — would be wrong on both counts.
      if (isForbidden(e)) {
        setForbidden(true);
        setData(null);
      } else {
        setError(e.message ?? String(e));
        void logError("app", e, { step: "load_usage", workspaceId });
      }
    } finally {
      setLoading(false);
    }
  }, [workspaceId, days]);

  useEffect(() => {
    void load();
  }, [load]);

  // Before the request, so nobody watches a spinner to be told no. `ready`
  // guards it: an unloaded permission list is not the same as an empty one, and
  // treating it as one would flash this screen at everybody on a slow start.
  if (ready && !can("billing.view")) return <NoAccess />;
  if (forbidden) return <NoAccess />;

  const sections = data ? SECTIONS.filter((s) => !s.when || s.when(data)) : [];

  return (
    <>
      <header className="bill-head">
        <div>
          <h1>Billing &amp; usage</h1>
          <p className="muted">
            What this workspace is on, what it has spent, and what is left.
          </p>
        </div>

        <div className="segmented" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              className={days === r.days ? "on" : ""}
              aria-pressed={days === r.days}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {error && <div className="card err">{error}</div>}

      {data && data.requests.open > 0 && (
        <div className="card">
          <div className="notice upgrade">
            You have a plan change being reviewed.{" "}
            <Link to="/profile">See its status</Link>
          </div>
        </div>
      )}

      {loading && !data ? (
        <p className="muted">Adding it up…</p>
      ) : !data ? null : (
        <div className={loading ? "bill-grid stale" : "bill-grid"}>
          {sections.map((section) => (
            <section
              key={section.id}
              className={section.size === "half" ? "card bill-card" : "card bill-card full"}
            >
              <div className="bill-card-head">
                <h2>{section.title}</h2>
                {section.lede && <p className="muted">{section.lede(data)}</p>}
              </div>
              {section.render(data)}
            </section>
          ))}

          {/* Where to actually change something. Deliberately the last thing on
              the page and deliberately a link rather than a button: there is no
              checkout to press (ADR-0009), and a button that opens a dialog
              which files a request would promise something faster than what
              happens. `billing.manage` is the permission to ask for a change;
              a manager can read this page and not act on it. */}
          <section className="card bill-card full bill-note-card">
            <h2>Changing your plan</h2>
            {can("billing.manage") ? (
              <p className="muted">
                Compare every tier and ask for a change on the{" "}
                <Link to="/plans">Pricing</Link> page. There is no checkout — you tell us what you
                want and how you intend to pay, and a person settles it with you.
              </p>
            ) : (
              <p className="muted">
                Asking for a plan change needs the <code>billing.manage</code> permission, which you
                do not have. An owner or an admin of this workspace can do it from{" "}
                <Link to="/plans">Pricing</Link>.
              </p>
            )}
          </section>
        </div>
      )}
    </>
  );
}
